/**
 * Cart Progress Bar System
 * Handles automatic gift management and progress bar updates
 */

class CartProgressBar {
  constructor(config = {}) {
    this.config = {
      shippingThreshold: config.shippingThreshold || 50,
      giftThreshold: config.giftThreshold || 100,
      discountThreshold: config.discountThreshold || 150,
      giftProductId: config.giftProductId || null,
      giftVariantId: config.giftVariantId || null,
      discountCode: config.discountCode || 'SAVE10',
      currency: config.currency || 'USD',
      ...config
    };
    
    this.currentCart = null;
    this.init();
  }

  async init() {
    await this.fetchCartState();
    this.bindEvents();
    this.updateProgressBar();
    this.manageGifts();
  }

  async fetchCartState() {
    try {
      const response = await fetch('/cart.js');
      this.currentCart = await response.json();
      return this.currentCart;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  bindEvents() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      this.handleCartUpdate(event.detail);
    });

    // Listen for AJAX cart changes
    this.observeCartChanges();
  }

  observeCartChanges() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const isCartRequest = /\/cart\//.test(entry.name);
          const isValidType = ['xmlhttprequest', 'fetch'].includes(entry.initiatorType);
          
          if (isCartRequest && isValidType) {
            setTimeout(() => this.handleCartChange(), 200);
          }
        });
      });
      
      observer.observe({ entryTypes: ["resource"] });
    }
  }

  async handleCartChange() {
    await this.fetchCartState();
    this.updateProgressBar();
    this.manageGifts();
    this.manageDiscounts();
  }

  handleCartUpdate(cartData) {
    this.currentCart = cartData;
    this.updateProgressBar();
    this.manageGifts();
    this.manageDiscounts();
  }

  updateProgressBar() {
    if (!this.currentCart) return;

    const cartTotal = this.currentCart.total_price / 100;
    const progressElement = document.querySelector('.progress-bar-fill');
    const messageElement = document.querySelector('.progress-message');
    const icons = document.querySelectorAll('.progress-icon');

    if (!progressElement || !messageElement) return;

    // Calculate progress percentage
    const topGoal = this.config.discountThreshold;
    const progressPercentage = Math.min((cartTotal / topGoal) * 100, 100);
    
    // Update progress bar
    progressElement.style.width = progressPercentage + '%';

    // Update message
    const message = this.generateProgressMessage(cartTotal);
    messageElement.innerHTML = message;

    // Update icons
    this.updateIcons(cartTotal, icons);

    // Trigger custom event
    document.dispatchEvent(new CustomEvent('progressbar:updated', {
      detail: { cartTotal, progressPercentage, message }
    }));
  }

  generateProgressMessage(cartTotal) {
    const { shippingThreshold, giftThreshold, discountThreshold } = this.config;

    if (cartTotal < shippingThreshold) {
      const remaining = shippingThreshold - cartTotal;
      return `Add <span class="progress-price">${this.formatCurrency(remaining)}</span> more for free shipping!`;
    } else if (cartTotal < giftThreshold) {
      const remaining = giftThreshold - cartTotal;
      return `Add <span class="progress-price">${this.formatCurrency(remaining)}</span> more to get a free gift!`;
    } else if (cartTotal < discountThreshold) {
      const remaining = discountThreshold - cartTotal;
      return `Add <span class="progress-price">${this.formatCurrency(remaining)}</span> more for 10% off!`;
    } else {
      return 'Congratulations! You\'ve unlocked all rewards! 🎉';
    }
  }

  updateIcons(cartTotal, icons) {
    const thresholds = [
      this.config.shippingThreshold,
      this.config.giftThreshold,
      this.config.discountThreshold
    ];

    icons.forEach((icon, index) => {
      if (cartTotal >= thresholds[index]) {
        icon.classList.add('achieved');
      } else {
        icon.classList.remove('achieved');
      }
    });
  }

  async manageGifts() {
    if (!this.config.giftVariantId || !this.currentCart) return;

    const cartTotal = this.currentCart.total_price / 100;
    const shouldHaveGift = cartTotal >= this.config.giftThreshold;
    const hasGift = this.hasGiftInCart();

    if (shouldHaveGift && !hasGift) {
      await this.addGiftToCart();
    } else if (!shouldHaveGift && hasGift) {
      await this.removeGiftFromCart();
    }
  }

  hasGiftInCart() {
    if (!this.currentCart || !this.config.giftVariantId) return false;
    
    return this.currentCart.items.some(item => 
      item.variant_id === this.config.giftVariantId ||
      (item.properties && item.properties._gift)
    );
  }

  async addGiftToCart() {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: this.config.giftVariantId,
            quantity: 1,
            properties: {
              '_gift': 'Free Gift - Threshold Reached',
              '_gift_threshold': this.config.giftThreshold
            }
          }]
        })
      });

      if (response.ok) {
        console.log('Gift added to cart');
        this.showNotification('Free gift added to your cart! 🎁', 'success');
        await this.fetchCartState();
      }
    } catch (error) {
      console.error('Error adding gift to cart:', error);
    }
  }

  async removeGiftFromCart() {
    try {
      const giftItem = this.currentCart.items.find(item => 
        item.variant_id === this.config.giftVariantId ||
        (item.properties && item.properties._gift)
      );

      if (!giftItem) return;

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: {
            [giftItem.key]: 0
          }
        })
      });

      if (response.ok) {
        console.log('Gift removed from cart');
        await this.fetchCartState();
      }
    } catch (error) {
      console.error('Error removing gift from cart:', error);
    }
  }

  async manageDiscounts() {
    if (!this.config.discountCode || !this.currentCart) return;

    const cartTotal = this.currentCart.total_price / 100;
    const shouldHaveDiscount = cartTotal >= this.config.discountThreshold;

    if (shouldHaveDiscount && !this.hasDiscountApplied()) {
      await this.applyDiscount();
    }
  }

  hasDiscountApplied() {
    if (!this.currentCart) return false;
    
    return this.currentCart.cart_level_discount_applications.some(
      discount => discount.title.toLowerCase().includes(this.config.discountCode.toLowerCase())
    );
  }

  async applyDiscount() {
    try {
      // Redirect to apply discount code
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.set('discount', this.config.discountCode);
      
      // Show notification instead of immediate redirect
      this.showNotification(
        `Discount code "${this.config.discountCode}" is now available! Apply it at checkout.`,
        'success'
      );
    } catch (error) {
      console.error('Error applying discount:', error);
    }
  }

  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `cart-progress-notification cart-progress-notification--${type}`;
    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-message">${message}</span>
        <button class="notification-close" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;

    // Add styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#d4edda' : '#d1ecf1'};
      color: ${type === 'success' ? '#155724' : '#0c5460'};
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid ${type === 'success' ? '#c3e6cb' : '#bee5eb'};
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      z-index: 10000;
      max-width: 350px;
      animation: slideIn 0.3s ease;
    `;

    // Add animation styles
    if (!document.getElementById('cart-progress-styles')) {
      const styles = document.createElement('style');
      styles.id = 'cart-progress-styles';
      styles.textContent = `
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .notification-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }
        .notification-close {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `;
      document.head.appendChild(styles);
    }

    document.body.appendChild(notification);

    // Auto remove after 5 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 5000);
  }

  formatCurrency(amount) {
    // Simple currency formatting - can be enhanced based on your needs
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.config.currency
    }).format(amount);
  }

  // Public method to update configuration
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.updateProgressBar();
  }

  // Public method to manually trigger update
  async refresh() {
    await this.fetchCartState();
    this.updateProgressBar();
    this.manageGifts();
    this.manageDiscounts();
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Configuration - Update these values based on your store settings
  const progressBarConfig = {
    shippingThreshold: 50,      // Free shipping threshold
    giftThreshold: 100,         // Free gift threshold  
    discountThreshold: 150,     // Discount threshold
    giftProductId: null,        // Replace with your gift product ID
    giftVariantId: 43491578380358,// Replace with your gift variant ID
    discountCode: 'SAVE10',     // Your discount code
    currency: 'INR'             // Your store currency
  };

  // Only initialize if progress bar exists on the page
  if (document.querySelector('.cart-progress-bar')) {
    window.cartProgressBar = new CartProgressBar(progressBarConfig);
  }
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartProgressBar;
}